import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ICONS from "../../../../assets/Icons";

import { getUsers } from "../../../../redux/features/Settings/Management/Users";
import accountNameStandardised from "../../../../utils/accountNamestandardised";
import globalText from "../../../../utils/globalText";
import TickerAnimation from "../../../_utils/TickerAnimation";
import Loader from "../../../Loader/Index";
import "./index.css";
import {
  GlobalConfiguration,
  PaginationConfiguration,
} from "../../../../utils/configurationProvider";

/**
 * TextAuthMatrixSelectUser - User selection component for testing authorization matrix.
 *
 * This component displays grade-grouped list of users for selection.
 * It operates in two modes:
 * - "users": Single selection for the primary approver (radio button UI)
 * - "previousUsers": Multi-selection for previous approvers (checkbox UI)
 *
 * Features:
 * - Fetches all users
 * - Groups users by grade for organized display
 * - Excludes already-selected user from previousUsers list
 * - Animated selection indicators with CSS transforms
 * - Ticker animation for long names/IDs
 * - Loading state with spinner
 *
 * @param {Object} props - Component props
 * @param {string} props.pageType - Mode: "users" (single select) or "previousUsers" (multi select)
 * @param {number} props.activeStep - Current step in parent wizard; triggers refetch on change
 * @param {Function} props.setSelectedUser - Setter for selected user (array with one user object)
 * @param {Array} props.selectedUser - Currently selected user [{id, first_name, last_name, grade, entity_id}]
 * @param {Array<string>} props.selectedPreviousUsersId - Array of selected previous approver IDs
 * @param {Function} props.setSelectedPreviousUsersId - Setter for selectedPreviousUsersId
 * @param {Array<Object>} props.selectedPreviousUsers - Array of selected previous approver objects
 * @param {Function} props.setSelectedPreviousUsers - Setter for selectedPreviousUsers
 * @returns {JSX.Element} Rendered user selection interface
 */
const TextAuthMatrixSelectUser = ({
  pageType,
  activeStep,
  setSelectedUser,
  selectedUser,
  selectedPreviousUsersId,
  setSelectedPreviousUsersId,
  selectedPreviousUsers,
  setSelectedPreviousUsers,
}) => {
  const dispatch = useDispatch();

  // Loading state for user fetch
  const [usersLoading, setUsersLoading] = useState(false);

  // Formatted user data grouped by grade: { "G1": [{...user}, ...], "G2": [...] }
  const [formattedUserData, setFormatedUserData] = useState({});

  /**
   * Fetches all users from API and groups them by grade.
   *
   * Process:
   * 1. Fetch first page to get total count
   * 2. Calculate total pages needed
   * 3. Fetch remaining pages in parallel (sequential loop)
   * 4. Combine all employees into single array
   * 5. Group by grade, excluding:
   *    - Users without a grade
   *    - Currently selected user (only in "previousUsers" mode to prevent self-selection)
   * 6. Update formattedUserData state
   *
   * Uses GlobalConfiguration.lengthCheckOne (1) for first page,
   * lengthCheckTwo (2) for subsequent pages,
   * and PaginationConfiguration.paginationBig for page size.
   */
  const fetchUsers = async () => {
    setUsersLoading(true);

    try {
      // Fetch first page to get total count and initial employees
      const firstPage = await dispatch(
        getUsers({
          page_no: GlobalConfiguration?.lengthCheckOne,
          page_size: PaginationConfiguration?.paginationBig,
        }),
      );

      if (firstPage?.payload?.status === true) {
        const totalCount = firstPage.payload.total_count;
        const totalPages = Math.ceil(
          totalCount / PaginationConfiguration?.paginationBig,
        );

        let allEmployees = [...firstPage.payload.employees];

        // Fetch remaining pages if more than one page
        if (totalPages > GlobalConfiguration?.lengthCheckOne) {
          for (
            let i = GlobalConfiguration?.lengthCheckTwo;
            i <= totalPages;
            i++
          ) {
            const res = await dispatch(
              getUsers({
                page_no: i,
                page_size: PaginationConfiguration?.paginationBig,
              }),
            );

            if (res?.payload?.status === true) {
              allEmployees = [...allEmployees, ...res.payload.employees];
            } else {
              console.error(`Failed to fetch page ${i}`);
            }
          }
        }

        // Group users by grade for organized display
        let groupedData = {};

        for (
          let i = GlobalConfiguration?.lengthCheckZero;
          i < allEmployees.length;
          i++
        ) {
          // Skip currently selected user in previousUsers mode to prevent self-selection
          if (
            selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.id &&
            selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.id ===
              allEmployees[i]?.id &&
            pageType === "previousUsers"
          ) {
            continue;
          } else {
            const employee = allEmployees[i];
            const grade = employee?.grade?.trim();

            if (grade) {
              if (!groupedData[grade]) {
                groupedData[grade] = [employee];
              } else {
                groupedData[grade].push(employee);
              }
            }
          }
        }

        setFormatedUserData(groupedData);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
    setUsersLoading(false);
  };

  /**
   * Refetch users when activeStep changes (e.g., navigating back to this step).
   * Dependency on activeStep ensures fresh data when returning to this step.
   */
  useEffect(() => {
    fetchUsers();
  }, [activeStep]);

  /**
   * Sync previousUsers selection when selectedUser changes.
   *
   * If the newly selected primary approver is already in the previous approvers list,
   * remove them from previous approvers (a user can't be both primary and previous).
   * Runs when selectedUser or selectedPreviousUsers changes.
   */
  useEffect(() => {
    if (pageType === "previousUsers") {
      if (
        selectedPreviousUsersId?.includes(
          selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.id,
        )
      ) {
        setSelectedPreviousUsersId(
          selectedPreviousUsersId.filter(
            (item) =>
              item !== selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.id,
          ),
        );
        setSelectedPreviousUsers(
          selectedPreviousUsers.filter(
            (item) =>
              item?.id !==
              selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.id,
          ),
        );
      }
    }
  }, [selectedUser, selectedPreviousUsers]);

  return (
    <>
      <div className="test-auth-sel-user-container px-padding-md Qlg:px-padding-base">
        <div className="test-auth-sel-user-main-div">
          <div className="test-auth-sel-user-top-div">
            {/* Dynamic title based on pageType */}
            <span className="text-base font-semibold text-text-primary">
              {pageType === "users"
                ? "Select User"
                : pageType === "previousUsers"
                  ? globalText?.PREVIOUS_APPROVERS_OPTIONAL
                  : undefined}
            </span>
            {/* Dynamic subtitle based on pageType */}
            <span className="text-xs font-normal text-text-secondary">
              {pageType === "users"
                ? "Select user who is approving the transaction."
                : pageType === "previousUsers"
                  ? "Select previous approver/s who have already approved the transaction."
                  : undefined}
            </span>
          </div>
          <div className="test-auth-sel-user-bottom-div">
            {/* Loading state */}
            {usersLoading ? (
              <div className="flex justify-center items-center py-4">
                <Loader />
              </div>
            ) : Object.entries(formattedUserData)?.length >
              GlobalConfiguration?.lengthCheckZero ? (
              // Render grade groups
              Object.entries(formattedUserData).map(([grade, arr]) => (
                <div
                  key={grade}
                  className="test-auth-sel-user-grade-main-div"
                >
                  <div className="test-auth-sel-user-grade-tilte text-text-primary">
                    {grade}
                  </div>

                  <div className="test-auth-sel-user-grade-cards-div">
                    {arr?.map((val) => {
                      const fullname =
                        [val?.first_name, val?.last_name]
                          .filter(Boolean)
                          .join(" ") || "";

                      return (
                        <div
                          key={val?.id}
                          className="bg-primary-nav rounded-border-radius-md cursor-pointer flex global-hover-white group "
                          onClick={() => {
                            if (pageType === "previousUsers") {
                              // Toggle selection for multi-select mode
                              if (
                                selectedPreviousUsersId?.includes(val?.id) ||
                                selectedUser?.[
                                  GlobalConfiguration?.lengthCheckZero
                                ]?.id === val?.id
                              ) {
                                // Deselect: remove from both ID and object arrays
                                setSelectedPreviousUsersId(
                                  selectedPreviousUsersId.filter(
                                    (item) => item !== val?.id,
                                  ),
                                );
                                setSelectedPreviousUsers(
                                  selectedPreviousUsers.filter(
                                    (item) => item?.id !== val?.id,
                                  ),
                                );
                              } else {
                                // Select: add to both ID and object arrays
                                setSelectedPreviousUsersId([
                                  ...selectedPreviousUsersId,
                                  val?.id,
                                ]);
                                setSelectedPreviousUsers([
                                  ...selectedPreviousUsers,
                                  val,
                                ]);
                              }
                            } else if (pageType === "users") {
                              // Single select: only set if different user
                              if (
                                selectedUser?.[
                                  GlobalConfiguration?.lengthCheckZero
                                ]?.id !== val?.id
                              ) {
                                setSelectedUser([val]);
                              }
                            }
                          }}
                        >
                          <div className="flex w-full group relative bg-primary-nav global-hover-white rounded-border-radius-md z-z-index-base transition-colors duration-animation-fast-s overflow-hidden items-center">
                            {/* Animated selection highlight - right portion (scale from right) */}
                            <div
                              class={`absolute bg-positive-bg group-hover:bg-positive-bg-hover z-z-index-header`}
                              style={{
                                transform:
                                  (val?.id ===
                                    selectedUser?.[
                                      GlobalConfiguration?.lengthCheckZero
                                    ]?.id &&
                                    pageType === "users") ||
                                  (selectedPreviousUsersId?.includes(val?.id) &&
                                    pageType === "previousUsers")
                                    ? "scale(1)"
                                    : "scale(0)",
                                transformOrigin: "right",
                                transition:
                                  (val?.id ===
                                    selectedUser?.[
                                      GlobalConfiguration?.lengthCheckZero
                                    ]?.id &&
                                    pageType === "users") ||
                                  (selectedPreviousUsersId?.includes(val?.id) &&
                                    pageType === "previousUsers")
                                    ? "transform var(--animation-fast-xs) ease-in"
                                    : "transform var(--animation-fast-s) ease-in",
                                height: "var(--height-full)",
                                width:
                                  "calc(var(--width-full) - var(--icon-lg))",
                                borderRadius:
                                  "var(--border-radius-sm) var(--border-none) var(--border-none) var(--border-radius-sm)",
                              }}
                            ></div>
                            {/* Animated selection highlight - left icon portion (scale from left) */}
                            <div
                              class={`absolute bg-positive-bg group-hover:bg-positive-bg-hover z-z-index-header`}
                              style={{
                                transform:
                                  (val?.id ===
                                    selectedUser?.[
                                      GlobalConfiguration?.lengthCheckZero
                                    ]?.id &&
                                    pageType === "users") ||
                                  (selectedPreviousUsersId?.includes(val?.id) &&
                                    pageType === "previousUsers")
                                    ? "scale(1)"
                                    : "scale(0)",
                                transformOrigin: "left",
                                transition:
                                  (val?.id ===
                                    selectedUser?.[
                                      GlobalConfiguration?.lengthCheckZero
                                    ]?.id &&
                                    pageType === "users") ||
                                  (selectedPreviousUsersId?.includes(val?.id) &&
                                    pageType === "previousUsers")
                                    ? "transform var(--animation-fast-xs) ease-in"
                                    : "transform var(--animation-fast-s) ease-in",
                                left: "calc(var(--left-full) - var(--icon-lg))",
                                height: "var(--height-full)",
                                width: "var(--icon-lg)",
                                borderRadius:
                                  "var(--border-none) var(--border-radius-sm) var(--border-radius-sm) var(--border-none)",
                              }}
                            ></div>

                            {/* User info: name with ticker animation, entity ID with ticker */}
                            <div className="flex flex-col justify-center pl-padding-md pr-padding-sm py-padding-sm flex-1 min-w-0 relative z-z-index-header gap-gap-xs">
                              <p className="text-text-primary text-font-sm font-semibold">
                                <TickerAnimation
                                  text={accountNameStandardised(fullname)}
                                />
                              </p>

                              <p className="text-text-secondary text-font-xs">
                                <TickerAnimation text={val?.entity_id} />
                              </p>
                            </div>

                            {/* Selection indicator icon - radio for users, checkbox for previousUsers */}
                            <div className="pr-padding-md relative z-z-index-header">
                              <img
                                src={
                                  pageType === "users"
                                    ? val?.id ===
                                      selectedUser?.[
                                        GlobalConfiguration?.lengthCheckZero
                                      ]?.id
                                      ? ICONS?.RADIO_BUTTON_SELECTED
                                      : ICONS?.RADIO_BUTTON_NOT_SELECTED
                                    : selectedPreviousUsersId?.includes(val?.id)
                                      ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                                      : ICONS?.NOT_SELECTED_WITH_GREY_CIRCLE
                                }
                                alt="selection"
                                className="h-icon-md w-icon-md"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-text-secondary">No users found.</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TextAuthMatrixSelectUser;
